// 用戶偏好語音服務 - 專門處理學習頁面的語音
import { TTSService } from './ttsService'
import { getUserVoicePreference } from './speech'

// 創建安全的默認語音偏好
function createDefaultVoicePreference() {
  return {
    service: 'voicevox',
    gender: 'female',
    voice_id: 2,
    voiceStyle: 'ノーマル',
    isVIP: false,
    timestamp: Date.now()
  }
}

// 驗證語音偏好對象的結構
function validateVoicePreference(pref: any) {
  if (!pref || typeof pref !== 'object') {
    return false
  }
  
  // 檢查必要屬性
  const hasService = typeof pref.service === 'string'
  const hasGender = typeof pref.gender === 'string'
  const hasvoice_id = typeof pref.voice_id === 'number'
  const hasVoiceStyle = typeof pref.voiceStyle === 'string'
  
  return hasService && hasGender && hasvoice_id && hasVoiceStyle
}

export class UserPreferenceTTSService implements TTSService {
  private baseTTSService: TTSService

  constructor(baseTTSService: TTSService) {
    this.baseTTSService = baseTTSService
  }

  async speak(text: string, options: {
    voice?: 'male' | 'female',
    language?: string,
    pitch?: number,
    rate?: number,
    voice_id?: number,
    fallbackToBrowser?: boolean,
    role?: string,
    userSessionId?: string,  // 新增：用戶會話 ID
    level?: string  // 新增：JLPT 等級 (n5, n4, n3, n2, n1)
  } = {}): Promise<void> {
    try {
      // console.group('🎵 用戶偏好語音服務')
      // console.log('輸入文本:', text)
      // console.log('輸入選項:', options)

      // 獲取用戶語音偏好
      let userVoicePreference = await getUserVoicePreference()
      
      // 強化的防護性檢查
      if (!userVoicePreference || typeof userVoicePreference !== 'object') {
        console.error('語音偏好獲取失敗:', userVoicePreference)
        userVoicePreference = createDefaultVoicePreference()
      }
      
      // 確保所有必要屬性都存在且類型正確
      if (typeof userVoicePreference.voiceStyle !== 'string') {
        console.error('🚨 voiceStyle 不是字符串:', typeof userVoicePreference.voiceStyle, userVoicePreference.voiceStyle)
        userVoicePreference.voiceStyle = 'ノーマル'
      }
      
      if (typeof userVoicePreference.service !== 'string') {
        console.error('🚨 service 不是字符串:', typeof userVoicePreference.service)
        userVoicePreference.service = 'voicevox'
      }
      
      if (typeof userVoicePreference.gender !== 'string') {
        console.error('🚨 gender 不是字符串:', typeof userVoicePreference.gender)
        userVoicePreference.gender = 'female'
      }
      
      if (typeof userVoicePreference.voice_id !== 'number') {
        console.error('🚨 voice_id 不是數字:', typeof userVoicePreference.voice_id)
        userVoicePreference.voice_id = 2
      }
      
      // 最終驗證
      if (!validateVoicePreference(userVoicePreference)) {
        console.error('🚨 語音偏好驗證失敗，使用默認值')
        userVoicePreference = createDefaultVoicePreference()
      }

      // console.log('🎵 用戶語音偏好:', userVoicePreference)

      // 使用用戶偏好的語音服務和性別
      const preferredService = userVoicePreference.service
      const preferredGender = userVoicePreference.gender
      const preferredvoice_id = userVoicePreference.voice_id
      const preferredVoiceStyle = userVoicePreference.voiceStyle

      // console.log(`🎵 語音播放: "${text}" | 性別: ${preferredGender} | 服務: ${preferredService} | 語音ID: ${preferredvoice_id}`)

      // 特殊處理包含 ';' 的文本
      const parts = text.split(';')
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim()
        
        if (part) {
          // 使用基礎語音服務播放（使用用戶偏好）
          await this.baseTTSService.speak(part, {
            voice: preferredGender as 'male' | 'female',
            voice_id: preferredvoice_id,
            language: 'ja-JP',
            role: options.role,
            fallbackToBrowser: true,
            userSessionId: options.userSessionId,
            level: options.level  // 傳遞 JLPT 等級
          })
        }
        
        // 只有在 parts 長度大於 1 且不是最後一個部分時，才停頓
        if (parts.length > 1 && i < parts.length - 1) {
          // console.log(`🕒 停頓開始，當前部分: ${part}, 下一部分: ${parts[i+1]}, 停頓時間: 500ms`)
          await new Promise(resolve => setTimeout(resolve, 500))
          // console.log(`🕒 停頓結束`)
        }
      }

      // console.log('🎵 用戶偏好語音播放完成')
      console.groupEnd()
      
    } catch (error) {
      console.error('🚨 UserPreferenceTTSService 執行失敗:', error)
      console.groupEnd()
      throw error
    }
  }

  async getAvailableVoices(): Promise<Array<{
    id: string,
    name: string,
    language: string,
    gender: 'male' | 'female'
  }>> {
    return this.baseTTSService.getAvailableVoices()
  }

  async isServiceAvailable?(): Promise<{
    available: boolean,
    message?: string,
    details?: any
  }> {
    return this.baseTTSService.isServiceAvailable?.() || { available: true }
  }
}
